import { CircularProgress } from '@mui/material'
import { createFileRoute, Navigate } from '@tanstack/react-router'
import { Building2, Calendar, ClipboardCheck, LayoutDashboard, Settings, Users } from 'lucide-react'
import { useContext, useMemo, useState } from 'react'
import Page from '~/components/Page'
import { GlobalTab } from '~/components/routes/visioReviews/GlobalTab'
import { InteractionsTab } from '~/components/routes/visioReviews/InteractionsTab'
import { OperatorsTab } from '~/components/routes/visioReviews/OperatorsTab'
import { PendingEvaluationsTab } from '~/components/routes/visioReviews/PendingEvaluationsTab'
import { StoresTab } from '~/components/routes/visioReviews/StoresTab'
import { getPreviousPeriodForPreset } from '~/components/routes/visioReviews/visioReviews.utils'
import { Button } from '~/components/ui/button'
import {
  DateRangePicker,
  type DateRangePreset,
  DEFAULT_PRESETS,
  getRangeForPreset,
} from '~/components/ui/date-range-picker'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '~/components/ui/tabs'
import { AuthContext } from '~/utils/context/auth.context'
import { useRights } from '~/utils/hooks'

export const Route = createFileRoute('/_authenticated/visio-reviews/')({
  component: VisioReviewsPage,
})

function VisioReviewsPage() {
  const navigate = Route.useNavigate()
  const { isLoading } = useContext(AuthContext)
  const hasVisioReviewsRight = useRights(['VISIO_REVIEWS'])
  const [preset, setPreset] = useState<DateRangePreset | null>('30d')
  const [dateRange, setDateRange] = useState(() => getRangeForPreset('30d'))
  // "Veille" uses a hardcoded yesterday range, so its date picker is hidden.
  const [activeTab, setActiveTab] = useState('veille')

  // Previous period is null for "Toujours" (unbounded past) → StoresTab /
  // OperatorsTab hide the trend column and omit previousStart/previousEnd
  // from their queries.
  // NOTE: must stay ABOVE the early return below — hooks must run unconditionally.
  const previousPeriod = useMemo(
    () => getPreviousPeriodForPreset(preset, dateRange),
    [preset, dateRange],
  )

  if (isLoading) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-sidebar px-6 py-10">
        <CircularProgress />
      </main>
    )
  }

  if (!hasVisioReviewsRight) {
    return <Navigate to="/dashboard" replace />
  }

  return (
    <Page>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">Contrôle Qualité des Interventions</h1>
          <Button variant="outline" onClick={() => navigate({ to: '/visio-reviews/admin' })}>
            <Settings className="h-4 w-4" />
            Administration
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <TabsList>
              <TabsTrigger value="veille" className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Interactions de la Veille
              </TabsTrigger>
              <TabsTrigger value="evaluation" className="flex items-center gap-2">
                <ClipboardCheck className="h-4 w-4" />
                Échanges à Évaluer
              </TabsTrigger>
              <TabsTrigger value="global" className="flex items-center gap-2">
                <LayoutDashboard className="h-4 w-4" />
                Vue d'ensemble
              </TabsTrigger>
              <TabsTrigger value="boutique" className="flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Par Boutique
              </TabsTrigger>
              <TabsTrigger value="operateur" className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                Par Opérateur
              </TabsTrigger>
            </TabsList>
            {activeTab !== 'veille' && (
              <DateRangePicker<DateRangePreset>
                value={dateRange}
                activePreset={preset}
                presets={DEFAULT_PRESETS}
                onChange={(range, newPreset) => {
                  setDateRange(range)
                  setPreset(newPreset)
                }}
              />
            )}
          </div>

          <TabsContent value="veille" className="mt-6">
            <InteractionsTab />
          </TabsContent>

          <TabsContent value="evaluation" className="mt-6">
            <PendingEvaluationsTab dateRange={dateRange} preset={preset} />
          </TabsContent>

          <TabsContent value="global" className="mt-6">
            <GlobalTab dateRange={dateRange} previousPeriod={previousPeriod} />
          </TabsContent>

          <TabsContent value="boutique" className="mt-6">
            <StoresTab dateRange={dateRange} previousPeriod={previousPeriod} />
          </TabsContent>

          <TabsContent value="operateur" className="mt-6">
            <OperatorsTab dateRange={dateRange} previousPeriod={previousPeriod} />
          </TabsContent>
        </Tabs>
      </div>
    </Page>
  )
}
